import { ANGLE_STEPS, cosA, dist2, iatan2, idiv, imax, imin, mulBp, sinA, withinRange } from '../math/fixed.ts';
import { cellAtWorld, heroWalkable } from '../grid.ts';
import { rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, Enemy, PlatesRules, PlatesState, SendaNpc } from '../types.ts';
import { damageEnemy, damageHero, ev, spawnEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';
import { turnOf } from './golems.ts';
import { pulseLand } from './brasa.ts';
import { shootTick } from './workshop.ts';
import { PLATE_FACE } from './tormo-layout.ts';

/**
 * Tormo (docs/sendas/artifice.md, chapter 5, act 4, the path's enemy; §5): in the workshop's yard, in the morning, the
 * sea below. What got in with the dust glows in one plate at a time (`order`; six, seven with the old dust; `dark` of
 * them out already, the scars). A blow on the glowing plate from its side (within `side` of its face) is a blow on the
 * nightmare (`per` of its life a plate); the hero's blows anywhere else land on Tormo's iron (`iron`): at 0 he breaks
 * and it is lost. The hero's whole kit counts from where the hero stands (its turrets and bots too: they carry its
 * name, not their place). The yard's three crossbows, worked by Grada's quarrymen, bolt the plate (`bolt` every
 * `every`) when it faces them; a rock that falls on one stops it `jam`.
 *
 * - Phase 1 (plates 1–2): he walks at the hero (`pace`, turning `turn` a tick) and his fist comes down where it stood
 *   (`fist`, warned 3 s, within a hex); every so often his stamp (`stamp`, a ring round him, warned 3 s).
 * - Phase 2 (3–4): rocks off the cliff (`rocks`, warned 3 s; one on the hero, one on a crossbow). A plate that goes out
 *   lets out cubs (`cubs`) that go for Nadd: the quarrymen before him take them first with their picks.
 * - Phase 3 (5–7): the glow moves to another place every `move` it isn't struck, and Tormo turns it away from the hero;
 *   once a plate, his hammer (`hammer`: both hands over his head, warned 3 s, two hexes round him).
 * - Soft time: from `softAt` the yard's edge falls under his feet (`soft`, on the hero), faster from `softFast`.
 *
 * An ogre in the sim (the render draws Tormo from `s.senda.plates`); this module moves him and the cubs (`platesHold`)
 * and strikes all their blows itself. Won with the last plate out; lost with the iron broken, Nadd down or the hero.
 *
 * Cost: one boss, three crossbows and a handful of cubs; the plate's side is one angle a blow.
 */

const fresh = (r: PlatesRules): PlatesState => ({
  id: 0,
  lit: 0,
  slot: r.order[r.dark] ?? 0,
  iron: r.iron,
  hitAt: 0,
  litAt: 0,
  ironSaid: false,
  fist: -1,
  fistAt: { x: 0, y: 0 },
  stamp: r.stamp.first,
  stampAt: -1,
  hammer: -1,
  rest: 0,
  hide: 0,
  hideTo: 0,
  rocks: r.rocks.first,
  spots: [],
  bowCd: r.every,
  bowOff: r.bows.map(() => 0),
  cubs: [],
  soft: r.softAt,
  dust: -1,
  shots: [0, 0, 0, 0],
  pend: [],
  free: -1,
  broke: -1,
});

const norm = (a: number): number => ((a % ANGLE_STEPS) + ANGLE_STEPS) % ANGLE_STEPS;

/** The plates out (the dark ones and those put out now) and all of them. */
export const platesOut = (r: PlatesRules, w: PlatesState): number => r.dark + w.lit;

/** The phase by the plates out: 1 (two), 2 (four), 3 (the rest). */
export const platesPhase = (r: PlatesRules, w: PlatesState): number => {
  const n = platesOut(r, w);
  return n < 2 ? 1 : n < 4 ? 2 : 3;
};

/** The glowing plate's face, in the world. */
export const plateFaceNow = (e: Pick<Enemy, 'facing'>, w: PlatesState): number => norm(e.facing + (PLATE_FACE[w.slot] ?? 0));

/** Whether a point is on the glowing plate's side. */
export const plateSide = (r: PlatesRules, w: PlatesState, e: Pick<Enemy, 'x' | 'y' | 'facing'>, x: number, y: number): boolean => {
  if (x === e.x && y === e.y) return false;
  const d = turnOf(plateFaceNow(e, w), iatan2(y - e.y, x - e.x));
  return d <= r.side && d >= -r.side;
};

/** The life Tormo's nightmare keeps while the plate glows (its blows can't take more than the plate's). */
const floorOf = (r: PlatesRules, w: PlatesState): number => imax(0, r.order.length - platesOut(r, w) - 1) * r.per + 1;

/**
 * A blow on Tormo (from combat's damageEnemy): on the glowing plate from its side, the nightmare's (no more than the
 * plate's left); the hero's anywhere else, his iron's (nothing to the nightmare); anything else off its side, nothing.
 */
export const plateTakes = (s: BattleState, e: Enemy, dmg: number, pid: string | undefined, fx: number | undefined, fy: number | undefined): number => {
  const r = s.rules?.senda?.plates;
  const w = s.senda?.plates;
  if (!r || !w || e.id !== w.id) return dmg;
  if (w.free >= 0 || w.broke >= 0) return 0;
  let x = fx;
  let y = fy;
  let hero = false;
  if (pid) {
    const h = s.heroes.find((o) => o.pid === pid);
    if (h && (x === undefined || y === undefined || withinRange(x, y, h.x, h.y, 500))) {
      hero = true;
      x = h.x;
      y = h.y;
    }
  }
  if (x === undefined || y === undefined) return 0;
  if (plateSide(r, w, e, x, y)) {
    w.hitAt = s.tick;
    return imax(0, imin(dmg, e.hp - floorOf(r, w)));
  }
  if (!hero) return 0;
  w.iron = imax(0, w.iron - dmg);
  ev(s, { t: 'senda', kind: 'iron', npc: '', x: e.x, y: e.y, n: w.iron });
  if (!w.ironSaid && w.iron * 2 <= r.iron) {
    w.ironSaid = true;
    w.pend.push('iron');
  }
  if (w.iron <= 0 && w.broke < 0) {
    w.broke = s.tick;
    ev(s, { t: 'senda', kind: 'tormoBreaks', npc: '', x: e.x, y: e.y });
  }
  return 0;
};

/** Whether an enemy is Tormo (its blows on the people are its own). */
export const platesOwn = (s: BattleState, id: number): boolean => !!id && s.senda?.plates?.id === id;

const npcOf = (s: BattleState, id: string): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === id && n.hp > 0 && !n.away);

/** Strikes heroes and people within `rad` of a point: `heroBp` of a hero's life, `blows` of a person's. */
const land = (s: BattleState, x: number, y: number, rad: number, heroBp: number, blows: number, e: Enemy | null): void => {
  for (const h of s.heroes) if (h.downTicks === 0 && withinRange(h.x, h.y, x, y, rad)) damageHero(s, h, mulBp(h.stats.maxHp, heroBp), e, 'physical', true);
  if (blows <= 0) return;
  for (const n of s.senda?.npcs ?? []) {
    if (n.hp <= 0 || n.away || !withinRange(n.x, n.y, x, y, rad) || s.rules?.senda?.npcs.find((d) => d.id === n.id)?.safe) continue;
    n.hp = imax(0, n.hp - blows);
    ev(s, { t: 'senda', kind: n.hp > 0 ? 'hit' : 'down', npc: n.id, x: n.x, y: n.y });
  }
};

/** A plate out: the next one glows; the phases; the cubs out of it in the second. */
const plateOut = (s: BattleState, r: PlatesRules, w: PlatesState, e: Enemy, cue: (c: string) => void): void => {
  const was = platesPhase(r, w);
  const slot = w.slot;
  w.lit++;
  const out = platesOut(r, w);
  ev(s, { t: 'senda', kind: 'plateOut', npc: '', x: e.x, y: e.y, n: slot });
  cue(`out${w.lit}`);
  if (out >= r.order.length) {
    w.free = s.tick;
    w.fist = w.stampAt = w.hammer = -1;
    ev(s, { t: 'senda', kind: 'tormoFree', npc: '', x: e.x, y: e.y });
    cue('free');
    return;
  }
  if (was === 2) {
    for (let k = 0; k < r.cubs.n; k++) {
      const c = spawnEnemy(s, 'goblin', 1, false, 0, e.x + rngRange(s.rng, -500, 500), e.y + rngRange(s.rng, -500, 500), r.cubs.hp);
      w.cubs.push(c.id);
    }
    ev(s, { t: 'senda', kind: 'cubsOut', npc: '', x: e.x, y: e.y, n: r.cubs.n });
    cue('cubs');
  }
  const now = platesPhase(r, w);
  if (now !== was) cue(`phase${now}`);
  w.slot = r.order[out] as number;
  w.hitAt = s.tick;
  w.litAt = s.tick;
  // The black dust of the plate out, over the whole yard, warned.
  w.dust = s.tick + r.dust.warn;
  ev(s, { t: 'senda', kind: 'dustWarn', npc: '', x: e.x, y: e.y, n: r.dust.warn });
  w.hammer = -1;
  if (out === r.order.length - 1) cue('last');
};

/** The phase 3 glow moving off, Tormo turning it from the hero. */
const moveGlow = (s: BattleState, r: PlatesRules, w: PlatesState, e: Enemy): void => {
  const slots = PLATE_FACE.length - (r.order.length < 7 ? 1 : 0);
  let to = rngInt(s.rng, slots - 1);
  if (to >= w.slot) to++;
  w.slot = to;
  w.hitAt = s.tick;
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (h) {
    // His plate turned from the hero: his facing so its face points away.
    w.hideTo = norm(iatan2(h.y - e.y, h.x - e.x) + ANGLE_STEPS / 2 - (PLATE_FACE[to] ?? 0));
    w.hide = s.tick + 18;
  }
  ev(s, { t: 'senda', kind: 'plateMove', npc: '', x: e.x, y: e.y, n: to });
};

/** Whether the plate faces a point (a crossbow). */
const faces = (r: PlatesRules, w: PlatesState, e: Enemy, x: number, y: number): boolean => plateSide(r, w, e, x, y);

const attacks = (s: BattleState, r: PlatesRules, w: PlatesState, e: Enemy, cue: (c: string) => void): void => {
  const h = s.heroes.find((x) => x.downTicks === 0);
  const ph = platesPhase(r, w);
  // The fist: winds where the hero stands in front of him.
  if (w.fist >= 0) {
    if (s.tick >= w.fist) {
      w.fist = -1;
      w.rest = s.tick + r.fist.rest;
      e.anim = 'attack';
      e.animT = 0;
      ev(s, { t: 'senda', kind: 'fist', npc: '', x: w.fistAt.x, y: w.fistAt.y });
      land(s, w.fistAt.x, w.fistAt.y, r.fist.r, r.fist.heroBp, r.fist.blows, e);
    }
  } else if (h && s.tick >= w.rest && w.stampAt < 0 && w.hammer < 0 && withinRange(h.x, h.y, e.x, e.y, r.fist.reach)) {
    const d = turnOf(e.facing, iatan2(h.y - e.y, h.x - e.x));
    if (d < 220 && d > -220) {
      w.fist = s.tick + r.fist.warn;
      w.fistAt = { x: h.x, y: h.y };
      ev(s, { t: 'senda', kind: 'fistWarn', npc: '', x: h.x, y: h.y, n: r.fist.warn });
    }
  }
  // The stamp: a ring round him.
  if (w.stampAt < 0 && w.fist < 0 && w.hammer < 0 && s.waveT >= w.stamp) {
    w.stampAt = s.tick + r.stamp.warn;
    ev(s, { t: 'senda', kind: 'stampWarn', npc: '', x: e.x, y: e.y, n: r.stamp.warn });
  } else if (w.stampAt >= 0 && s.tick >= w.stampAt) {
    w.stampAt = -1;
    w.stamp = s.waveT + r.stamp.every;
    ev(s, { t: 'senda', kind: 'stamp', npc: '', x: e.x, y: e.y });
    land(s, e.x, e.y, r.stamp.r, r.stamp.heroBp, r.stamp.blows, e);
  }
  // The hammer, once a plate in the third phase.
  if (ph === 3) {
    if (w.hammer === -1 && w.fist < 0 && w.stampAt < 0 && s.tick >= w.litAt + r.hammer.at) {
      w.hammer = s.tick + r.hammer.warn;
      ev(s, { t: 'senda', kind: 'hammerWarn', npc: '', x: e.x, y: e.y, n: r.hammer.warn });
      cue('hammer');
    } else if (w.hammer >= 0 && s.tick >= w.hammer) {
      w.hammer = -2;
      e.anim = 'attack';
      e.animT = 0;
      ev(s, { t: 'senda', kind: 'hammer', npc: '', x: e.x, y: e.y });
      land(s, e.x, e.y, r.hammer.r, r.hammer.heroBp, r.hammer.blows, e);
    }
  }
  // Rocks off the cliff from the second phase.
  if (ph >= 2 && s.waveT >= w.rocks) {
    w.rocks = s.waveT + r.rocks.every;
    const t = s.tick + r.rocks.warn;
    if (h) {
      const x = h.x + rngRange(s.rng, -300, 300);
      const y = h.y + rngRange(s.rng, -300, 300);
      w.spots.push({ x, y, t });
      ev(s, { t: 'senda', kind: 'pulseWarn', npc: '', x, y, n: r.rocks.warn });
    }
    const b = r.bows[rngInt(s.rng, r.bows.length)] as { x: number; y: number };
    w.spots.push({ x: b.x, y: b.y, t });
    ev(s, { t: 'senda', kind: 'pulseWarn', npc: '', x: b.x, y: b.y, n: r.rocks.warn });
  }
  // Soft time: the yard's edge under the hero.
  if (h && s.waveT >= w.soft) {
    w.soft = s.waveT + (s.waveT >= r.softFast ? r.soft.fast : r.soft.every);
    const t = s.tick + r.soft.warn;
    w.spots.push({ x: h.x, y: h.y, t });
    ev(s, { t: 'senda', kind: 'pulseWarn', npc: '', x: h.x, y: h.y, n: r.soft.warn });
  }
};

/** Rocks that land on a crossbow stop it. */
const bowsHit = (s: BattleState, r: PlatesRules, w: PlatesState): void => {
  for (const p of w.spots) {
    if (p.t !== s.tick) continue;
    r.bows.forEach((b, i) => {
      if (withinRange(b.x, b.y, p.x, p.y, r.rocks.r)) {
        w.bowOff[i] = s.tick + r.jam;
        ev(s, { t: 'senda', kind: 'bowJam', npc: '', x: b.x, y: b.y, n: i });
      }
    });
  }
};

export const platesTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.plates;
  const st = s.senda;
  if (!r || !st) return;
  const w = (st.plates ??= fresh(r));
  if (!w.id) {
    const e = spawnEnemy(s, 'ogre', 1, false, 0, r.tormoAt.x, r.tormoAt.y, (r.order.length - r.dark) * r.per + 1);
    e.facing = iatan2(r.naddAt.y - e.y, r.naddAt.x - e.x);
    e.anim = 'idle';
    w.id = e.id;
    s.bossId = e.id;
  }
  if (w.pend.length) {
    for (const c of w.pend) cue(c);
    w.pend.length = 0;
  }
  if (!s.started || w.free >= 0 || w.broke >= 0) return;
  const e = s.enemies.find((x) => x.id === w.id);
  if (!e) return;
  if (e.hp <= floorOf(r, w)) plateOut(s, r, w, e, cue);
  if (w.free >= 0) return;
  // (Not before this plate's hammer has come down.)
  if (platesPhase(r, w) === 3 && w.hammer === -2 && s.tick - w.hitAt >= r.move) moveGlow(s, r, w, e);
  attacks(s, r, w, e, cue);
  if (w.dust >= 0 && s.tick >= w.dust) {
    w.dust = -1;
    ev(s, { t: 'senda', kind: 'dust', npc: '', x: e.x, y: e.y });
    for (const h of s.heroes) if (h.downTicks === 0) damageHero(s, h, mulBp(h.stats.maxHp, r.dust.heroBp), e, 'shadow', true);
  }
  bowsHit(s, r, w);
  pulseLand(s, w.spots, r.rocks, 0);
  // The crossbows on the plate when it faces them.
  if (--w.bowCd <= 0) {
    w.bowCd = r.every;
    r.bows.forEach((b, i) => {
      if (s.tick < (w.bowOff[i] as number) || !faces(r, w, e, b.x, b.y)) return;
      ev(s, { t: 'senda', kind: 'bolt', npc: `bow${i}`, x: e.x, y: e.y, n: i });
      damageEnemy(s, e, r.bolt, { pid: '', pierce: true, fromX: b.x, fromY: b.y });
    });
  }
  w.cubs = w.cubs.filter((id) => s.enemies.some((x) => x.id === id && x.hp > 0));
  // The people: Dunna's bolts, Adela's and the quarryman's picks.
  for (const n of st.npcs) {
    if (n.hp <= 0 || n.away) continue;
    if (n.flinch > 0) n.flinch--;
    const k = n.id === 'dunna' ? 0 : n.id === 'adela' ? 1 : n.id === 'quarry' ? 2 : -1;
    if (k < 0) continue;
    w.shots[k] = shootTick(s, n, k === 0 ? r.dunna : r.pick, w.shots[k] as number);
  }
};

/** Tormo's move and the cubs' (from the enemies' tick; true: this module moves it). */
export const platesHold = (s: BattleState, e: Enemy, px: number): boolean => {
  const r = s.rules?.senda?.plates;
  const w = s.senda?.plates;
  if (!r || !w) return false;
  if (e.id === w.id) {
    if (w.free >= 0 || w.broke >= 0) {
      e.anim = 'idle';
      return true;
    }
    if (w.fist >= 0 || w.stampAt >= 0 || w.hammer >= 0) {
      e.anim = 'cast';
      return true;
    }
    if (e.anim === 'attack' && e.animT > 10) e.anim = 'idle';
    if (s.tick < w.hide) {
      const d = turnOf(e.facing, w.hideTo);
      const t = r.turn * 4;
      e.facing = norm(e.facing + (d > t ? t : d < -t ? -t : d));
      e.anim = 'idle';
      return true;
    }
    const h = s.heroes.find((x) => x.downTicks === 0);
    if (!h) return true;
    const want = iatan2(h.y - e.y, h.x - e.x);
    const d = turnOf(e.facing, want);
    e.facing = norm(e.facing + (d > r.turn ? r.turn : d < -r.turn ? -r.turn : d));
    if (withinRange(e.x, e.y, h.x, h.y, r.fist.reach - 300) || d > 200 || d < -200) {
      if (e.anim === 'walk') e.anim = 'idle';
      return true;
    }
    const nx = e.x + idiv(cosA(e.facing) * r.pace, 10000);
    const ny = e.y + idiv(sinA(e.facing) * r.pace, 10000);
    if (heroWalkable(s, cellAtWorld(s, nx, ny))) {
      e.x = nx;
      e.y = ny;
      e.anim = 'walk';
    } else e.anim = 'idle';
    return true;
  }
  if (!w.cubs.includes(e.id)) return false;
  // A cub for Nadd: the quarrymen before him take it first.
  let best: SendaNpc | undefined;
  let bd = 0x7fffffff;
  for (const id of ['quarry', 'adela', 'nadd']) {
    const n = npcOf(s, id);
    if (!n) continue;
    const dd = dist2(e.x, e.y, n.x, n.y);
    if (dd < bd) {
      bd = dd;
      best = n;
    }
    // (The front's first: Nadd only when nobody stands before him.)
    if (id !== 'nadd') break;
  }
  if (!best) return false;
  if (withinRange(e.x, e.y, best.x, best.y, 650)) {
    e.facing = iatan2(best.y - e.y, best.x - e.x);
    if (e.cd === 0) {
      e.cd = e.reload;
      e.anim = 'attack';
      e.animT = 0;
    }
    return true;
  }
  e.anim = 'walk';
  stepToward(e, best.x, best.y, px);
  return true;
};

/** The plates out of all. */
export const platesGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.plates;
  const w = s.senda?.plates;
  if (!r) return { done: 0, need: 1 };
  return { done: w ? platesOut(r, w) : r.dark, need: r.order.length };
};

export const platesResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.senda?.plates;
  const w = s.senda?.plates;
  if (!r || !w) return null;
  if (w.broke >= 0) return 'lost';
  return w.free >= 0 && s.tick >= w.free + r.end ? 'won' : null;
};
